// src/Payroll_Management/PayRoll_Report/ConveyanceAllowanceReport.jsx
import { useEffect, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  FileSpreadsheet,
  Hash,
  Type,
  Calendar,
  MapPin,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

// Accepts:
// - "" => ""
// - "yyyy-mm-dd" => "dd/MM/yyyy"
// - "dd/MM/yyyy" => "dd/MM/yyyy"
function toDDMMYYYY(input) {
  const v = s(input);
  if (!v) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [yy, mm, dd] = v.split("-");
    return `${dd}/${mm}/${yy}`;
  }
  return v; // fallback (keeps legacy text)
}

// quick debounce
function useDebouncedCallback(cb, delay = 250) {
  const t = useRef(null);
  return (...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => cb(...args), delay);
  };
}

const ConveyanceAllowanceReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branchTypeLS = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "HO";

  // meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState(["Select"]);
  const [companyName, setCompanyName] = useState("");

  // filters (ASP fields)
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [branchId, setBranchId] = useState("Select");
  const [month, setMonth] = useState("Select");
  const [year, setYear] = useState("Select");
  const [fromDate, setFromDate] = useState(""); // store as yyyy-mm-dd for input[type=date]
  const [toDate, setToDate] = useState("");

  // suggestions
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);

  // table
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : (Conveyance Allowance Report)", []);
  const canViewAll = s(branchTypeLS).toUpperCase() === "HO";

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const selectBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledSelectClass = !canViewAll ? "opacity-70 cursor-not-allowed" : "";

  const validateDates = () => {
    // Accept empty.
    if (!fromDate || !toDate) return true;

    // Compare in yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromDate) && /^\d{4}-\d{2}-\d{2}$/.test(toDate)) {
      if (fromDate > toDate) {
        toast.warn("From Date cannot be greater than To Date");
        return false;
      }
    }
    return true;
  };

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchTypeLS)) {
      toast.warn("Missing login branch/branch type. Please re-login.");
      return;
    }

    try {
      setMetaLoading(true);

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/meta`, {
        params: {
          finYearId: toInt(finYearId),
          branchId: toInt(loginBranchId),
          branchType: s(branchTypeLS),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      const m = res.data;
      setBranches(m.branches || []);
      setYears(m.years || ["Select"]);
      setCompanyName(m.companyName || "");

      setMonth(m.defaultMonth || "Select");
      setYear(m.defaultYear || "Select");

      // default branch:
      const defBr = m.defaultBranchId ? String(m.defaultBranchId) : "Select";
      setBranchId(canViewAll ? defBr : String(toInt(loginBranchId) || defBr));
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta load error");
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchData = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchTypeLS)) {
      toast.warn("Missing login branch/branch type. Please re-login.");
      return;
    }
    if (!validateDates()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: s(loginBranchId),
          branchType: s(branchTypeLS),
          branchId: s(branchId),
          empCode: s(empCode),
          empName: s(empName),
          month: s(month),
          year: s(year),
          fromDate: toDDMMYYYY(fromDate),
          toDate: toDDMMYYYY(toDate),
          page: p,
          pageSize,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load report");
        return;
      }

      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(res.data.page || p);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Data load error");
    } finally {
      setLoading(false);
    }
  };

  // autocomplete (debounced)
  const debouncedCodeSuggest = useDebouncedCallback(async (q) => {
    try {
      if (!s(q) || s(q).length < 1) {
        setCodeSug([]);
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/empcode-suggest`, {
        params: {
          finYearId: toInt(finYearId),
          branchType: s(branchTypeLS),
          branchId: canViewAll ? s(branchId) : s(loginBranchId),
          q: s(q),
        },
      });

      if (res?.data?.success) setCodeSug(res.data.items || []);
    } catch {
      // silent
    }
  }, 250);

  const debouncedNameSuggest = useDebouncedCallback(async (q) => {
    try {
      if (!s(q) || s(q).length < 1) {
        setNameSug([]);
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/empname-suggest`, {
        params: {
          finYearId: toInt(finYearId),
          branchType: s(branchTypeLS),
          branchId: canViewAll ? s(branchId) : s(loginBranchId),
          q: s(q),
        },
      });

      if (res?.data?.success) setNameSug(res.data.items || []);
    } catch {
      // silent
    }
  }, 250);

  const onSearch = async (e) => {
    e?.preventDefault?.();
    if (!validateDates()) return;
    setPage(1);
    await fetchData(1);
  };

  const onRefresh = async () => {
    await fetchData(page);
  };

  const resetFilters = async () => {
    setEmpCode("");
    setEmpName("");
    setFromDate("");
    setToDate("");

    // keep meta defaults for month/year/branch
    await loadMeta();
    setPage(1);
    await fetchData(1);
  };

  // Excel (client-side HTML-as-XLS) using print-data endpoint
  const downloadExcel = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validateDates()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/print-data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: s(loginBranchId),
          branchType: s(branchTypeLS),
          branchId: s(branchId),
          empCode: s(empCode),
          empName: s(empName),
          month: s(month),
          year: s(year),
          fromDate: toDDMMYYYY(fromDate),
          toDate: toDDMMYYYY(toDate),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Export failed");
        return;
      }

      const allRows = res.data.rows || [];
      const brName =
        (branches.find((b) => String(b.fld_branchid) === String(branchId))?.fld_branchname) ||
        (canViewAll ? "Select" : "Login Branch");

      const monthText = (() => {
        const m = toInt(month);
        const names = [
          "", "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ];
        return names[m] || month || "Select";
      })();

      const title = "Conveyance Allowance Report";
      const ym = `${year || "Select"}-${monthText || "Select"}`;

      const cols = [
        "S.No",
        "Employee Code",
        "Employee Name",
        "Branch",
        "Designation",
        "Conveyance Allowance",
      ];

      const escapeHtml = (x) =>
        s(x)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      const tableRows = allRows
        .map((r, idx) => {
          const code = escapeHtml(r.fld_employeecode);
          const name = escapeHtml(r.fld_employee);
          const branch = escapeHtml(r.fld_branchname);
          const desig = escapeHtml(r.fld_designationname);
          const amt = escapeHtml(r.fld_conveyanceallowance);
          return `
            <tr>
              <td style="text-align:center;">${idx + 1}</td>
              <td style="text-align:center;">${code}</td>
              <td style="text-align:center;">${name}</td>
              <td style="text-align:center;">${branch}</td>
              <td style="text-align:center;">${desig}</td>
              <td style="text-align:right;">${amt}</td>
            </tr>
          `;
        })
        .join("");

      const html = `
        <html>
          <head>
            <meta charset="utf-8" />
          </head>
          <body>
            <table border="1" style="font-size:24px" align="center">
              <tr><td align="center" colspan="${cols.length}"><b>${escapeHtml(companyName || "")}</b></td></tr>
            </table>
            <table border="1" style="font-size:16px" align="center">
              <tr><td align="center" colspan="${cols.length}"><b>${title}</b></td></tr>
            </table>
            <table border="1" style="font-size:16px" align="left">
              <tr>
                <td align="left" colspan="${cols.length - 1}"><b>Branch Name : ${escapeHtml(brName)}</b></td>
                <td><b>Year-Month : ${escapeHtml(ym)}</b></td>
              </tr>
            </table>
            <br/><br/>
            <table border="1" style="font-size:12px" width="100%">
              <thead>
                <tr>
                  ${cols.map((c) => `<th style="background:#7296da;color:#fff;">${escapeHtml(c)}</th>`).join("")}
                </tr>
              </thead>
              <tbody>${tableRows || ""}</tbody>
            </table>
            <br/>
            <table border="1" style="font-size:16px" align="left">
              <tr><td align="center" colspan="${cols.length}">Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com</td></tr>
            </table>
          </body>
        </html>
      `;

      const blob = new Blob([html], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Conveyance Allowance Report.xls";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Export error");
    } finally {
      setLoading(false);
    }
  };

// import { useNavigate } from "react-router-dom";
const navigate = useNavigate();

const printReport = () => {
  if (!s(finYearId)) {
    toast.warn("Please select Financial Year.");
    return;
  }
  if (!validateDates()) return;

  // Branch restriction like ASP
  const effectiveBranchId = canViewAll ? s(branchId) : s(loginBranchId);

  // ✅ Go to print page in same tab (no popup)
  const qs = new URLSearchParams({
    finYearId: s(finYearId),
    loginBranchId: s(loginBranchId),
    branchType: s(branchTypeLS),
    branchId: effectiveBranchId,
    empCode: s(empCode),
    empName: s(empName),
    month: s(month),
    year: s(year),
    fromDate: s(fromDate), // keep as yyyy-mm-dd; print page will convert to dd/MM/yyyy before API call
    toDate: s(toDate),
  }).toString();

  navigate(`/PayRoll/ConveyanceAllowanceReportPrint?${qs}`);
};


  // initial
  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch on page change
  useEffect(() => {
    // don’t auto-fetch until meta loaded once
    if (!s(finYearId)) return;
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // if branch changes, clear suggestions
  useEffect(() => {
    setCodeSug([]);
    setNameSug([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONVEYANCE ALLOWANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Conveyance Allowance Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Export / Print / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={onRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={downloadExcel} className={ghostBtn} disabled={loading || metaLoading}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>

              <button onClick={printReport} className={ghostBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={resetFilters} className={primaryBtn} disabled={loading || metaLoading}>
                <RefreshCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={onSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Code */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => {
                      setEmpCode(e.target.value);
                      debouncedCodeSuggest(e.target.value);
                    }}
                    list="empCodeList"
                    placeholder="Type employee code..."
                    className={inputBase}
                    autoComplete="off"
                  />
                  <datalist id="empCodeList">
                    {codeSug.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Employee Name */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => {
                      setEmpName(e.target.value);
                      debouncedNameSuggest(e.target.value);
                    }}
                    list="empNameList"
                    placeholder="Type employee name..."
                    className={inputBase}
                    autoComplete="off"
                  />
                  <datalist id="empNameList">
                    {nameSug.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={`${selectBase} ${disabledSelectClass}`}
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    disabled={!canViewAll}
                    title={!canViewAll ? "Branch locked for non-HO users" : ""}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>Month</label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select className={selectBase} value={month} onChange={(e) => setMonth(e.target.value)}>
                    <option value="Select">Select</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
              </div>

              {/* From Date */}
              <div>
                <label className={labelBase}>From Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
                <div className={`mt-1 text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                  (Sent to API as dd/MM/yyyy)
                </div>
              </div>

              {/* To Date */}
              <div>
                <label className={labelBase}>To Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>Year</label>
                <select className={selectBase} value={year} onChange={(e) => setYear(e.target.value)}>
                  {(years || ["Select"]).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search button */}
              <div className="flex items-end">
                <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Conveyance Allowance</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_employeecode}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_conveyanceallowance}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Small note */}
          <div className={`mt-4 text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}>
            Tip: Non-HO users can’t change Branch (same as ASP). Excel/Print fetch full data (no pagination).
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default ConveyanceAllowanceReport;
